import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState('online'); // online, offline, busy
  const messagesEndRef = useRef(null);

  const initialMessages = [
  {
    id: 1,
    sender: 'bot',
    message: `Hi there! 👋 I'm Aqua, your Mava assistant. How can I help you today?`, timestamp: new Date(), avatar: "https://images.unsplash.com/photo-1602841240819-aeb3408df442", avatarAlt: 'Friendly customer service representative with headset smiling at camera'
  }];


  const quickReplies = [
  { id: 1, text: 'Product Information', icon: 'Package' },
  { id: 2, text: 'Bulk Orders', icon: 'ShoppingCart' },
  { id: 3, text: 'Customization', icon: 'Palette' },
  { id: 4, text: 'Shipping Details', icon: 'Truck' },
  { id: 5, text: 'Technical Support', icon: 'Settings' }];


  const botResponses = {
    'Product Information': `Great! Here's what makes our bottles special:\n\n🔹 Steel Heritage: Premium stainless steel with 24-hour insulation\n🔹 Smart Innovation: IoT-enabled with hydration tracking\n🔹 Insulated Performance: Advanced temperature retention\n🔹 Limited Editions: Exclusive designs with sustainable materials\n\nWhich collection interests you most?`,
    'Bulk Orders': `Perfect for corporate needs! 🏢\n\nOur bulk order benefits:\n✅ Volume discounts starting at 50+ units\n✅ Custom branding and engraving\n✅ Dedicated account manager\n✅ Flexible payment terms\n✅ Priority delivery\n\nWould you like me to connect you with our B2B specialist?`,
    'Customization': `We love making bottles uniquely yours! 🎨\n\nCustomization options:\n🔸 Laser engraving (min 25 units)\n🔸 UV printing (min 100 units)\n🔸 Custom colors and finishes\n🔸 Corporate branding packages\n🔸 Gift packaging options\n\nWhat type of customization are you looking for?`,
    'Shipping Details': `We've got you covered across India! 🚚\n\nShipping info:\n📦 Free shipping on orders ₹2,000+\n⚡ Express delivery in 1-2 days (major cities)\n🌍 Standard delivery in 3-5 days\n📍 International shipping available\n💰 COD available for orders under ₹10,000\n\nNeed help with a specific delivery location?`,
    'Technical Support': `I'm here to help with any technical questions! 🔧\n\nCommon support topics:\n🔋 Smart bottle setup and pairing\n📱 Mobile app troubleshooting\n🧼 Cleaning and maintenance guides\n🛡️ Warranty claims and repairs\n📞 Product registration\n\nWhat technical issue can I assist you with?`
  };

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      setMessages(initialMessages);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (!inputMessage?.trim()) return;

    const newMessage = {
      id: Date.now(),
      sender: 'user',
      message: inputMessage,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponse = {
        id: Date.now() + 1,
        sender: 'bot',
        message: getBotResponse(inputMessage),
        timestamp: new Date(),
        avatar: "https://images.unsplash.com/photo-1602841240819-aeb3408df442",
        avatarAlt: 'Friendly customer service representative with headset smiling at camera'
      };
      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const getBotResponse = (userMessage) => {
    const message = userMessage?.toLowerCase();

    if (message?.includes('price') || message?.includes('cost')) {
      return `Our bottles range from ₹2,499 to ₹5,999 depending on the collection:\n\n💰 Steel Heritage: ₹2,499\n💰 Insulated Performance: ₹3,299\n💰 Smart Innovation: ₹4,999\n💰 Limited Editions: ₹5,999\n\nBulk discounts available! Would you like a detailed quote?`;
    } else if (message?.includes('delivery') || message?.includes('shipping')) {
      return botResponses?.['Shipping Details'];
    } else if (message?.includes('smart') || message?.includes('app')) {
      return `Our Smart bottles are amazing! 📱\n\nFeatures:\n🔹 Hydration tracking and reminders\n🔹 Temperature monitoring\n🔹 Health insights and analytics\n🔹 6-month battery life\n🔹 Bluetooth connectivity\n🔹 Companion mobile app\n\nThe app is available on both iOS and Android. Need help with setup?`;
    } else if (message?.includes('warranty') || message?.includes('guarantee')) {
      return `We stand behind our quality! 🛡️\n\nWarranty coverage:\n✅ 2-year manufacturer warranty\n✅ Leak-proof guarantee\n✅ Insulation performance promise\n✅ Smart components covered separately\n✅ Easy warranty claims process\n\nHave a specific warranty question?`;
    } else {
      return `Thanks for your message! I'd be happy to help you with that. For detailed assistance, I can connect you with one of our product specialists who can provide personalized recommendations.\n\nWould you like me to:\n🔹 Schedule a callback\n🔹 Send you our product catalog\n🔹 Connect you with sales team\n🔹 Help with something else?`;
    }
  };

  const handleQuickReply = (reply) => {
    const message = {
      id: Date.now(),
      sender: 'user',
      message: reply?.text,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, message]);
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = {
        id: Date.now() + 1,
        sender: 'bot',
        message: botResponses?.[reply?.text],
        timestamp: new Date(),
        avatar: "https://images.unsplash.com/photo-1602841240819-aeb3408df442",
        avatarAlt: 'Friendly customer service representative with headset smiling at camera'
      };
      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center brand-transition ${
        isOpen ?
        'bg-red-500 hover:bg-red-600' : 'bg-gradient-to-br from-primary to-accent hover:from-primary/90 hover:to-accent/90'}`
        }>

        <Icon
          name={isOpen ? "X" : "MessageCircle"}
          size={24}
          color="white" />

        
        {/* Online Status Indicator */}
        {!isOpen && chatStatus === 'online' &&
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        }
      </motion.button>
      {/* Chat Window */}
      <AnimatePresence>
        {isOpen &&
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-24 right-6 z-40 w-96 max-w-[calc(100vw-3rem)] h-[32rem] bg-card rounded-2xl liquid-shadow border border-border overflow-hidden">

            {/* Chat Header */}
            <div className="bg-gradient-to-r from-primary to-accent p-4 text-white">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Icon name="Droplets" size={20} />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-poppins font-semibold">Mava Support</h4>
                  <p className="text-xs opacity-90">
                    {chatStatus === 'online' ? 'Online • Typically replies instantly' : 'Offline • We\'ll get back to you soon'}
                  </p>
                </div>
                <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg brand-transition">

                  <Icon name="Minimize2" size={16} />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 p-4 h-80 overflow-y-auto space-y-4">
              {messages?.map((message) =>
            <div
              key={message?.id}
              className={`flex ${message?.sender === 'user' ? 'justify-end' : 'justify-start'}`}>

                  <div className={`flex items-end space-x-2 max-w-[80%] ${
              message?.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`
              }>
                    {message?.sender === 'bot' &&
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img
                    src={message?.avatar}
                    alt={message?.avatarAlt}
                    className="w-full h-full object-cover" />

                      </div>
                }
                    <div className={`px-4 py-2 rounded-2xl ${
                message?.sender === 'user' ? 'bg-primary text-white rounded-br-md' : 'bg-muted text-text-primary rounded-bl-md'}`
                }>
                      <p className="text-sm whitespace-pre-line">{message?.message}</p>
                      <p className={`text-xs mt-1 ${
                  message?.sender === 'user' ? 'text-white/70' : 'text-text-secondary'}`
                  }>
                        {message?.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
            )}

              {/* Typing Indicator */}
              {isTyping &&
            <div className="flex justify-start">
                  <div className="flex items-end space-x-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img
                    src="https://images.unsplash.com/photo-1632923945736-f8d20278a302"
                    alt="Customer service representative typing response"
                    className="w-full h-full object-cover" />

                    </div>
                    <div className="bg-muted px-4 py-2 rounded-2xl rounded-bl-md">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </div>
                </div>
            }

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            {messages?.length <= 1 &&
          <div className="px-4 pb-2">
                <p className="text-xs text-text-secondary mb-2">Quick options:</p>
                <div className="flex flex-wrap gap-2">
                  {quickReplies?.slice(0, 3)?.map((reply) =>
              <button
                key={reply?.id}
                onClick={() => handleQuickReply(reply)}
                className="flex items-center space-x-1 px-3 py-1 bg-surface hover:bg-muted text-xs rounded-full brand-transition">

                      <Icon name={reply?.icon} size={12} />
                      <span>{reply?.text}</span>
                    </button>
              )}
                </div>
              </div>
          }

            {/* Message Input */}
            <div className="p-4 border-t border-border">
              <div className="flex space-x-2">
                <Input
                type="text"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                className="flex-1" />

                <Button
                onClick={handleSendMessage}
                disabled={!inputMessage?.trim()}
                iconName="Send"
                size="sm"
                className="px-3" />

              </div>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </>);

};

export default LiveChatWidget;